import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import {
  ResponsiveContainer, ComposedChart, Line, Area, Bar, XAxis, YAxis,
  Tooltip, CartesianGrid, ReferenceLine, Cell
} from 'recharts';

interface PricePoint {
  date: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

interface ChartDataPoint extends PricePoint {
  sma50?: number;
  sma200?: number;
  bbUpper?: number;
  bbMiddle?: number;
  bbLower?: number;
  tenkan?: number;
  kijun?: number;
  senkouA?: number;
  senkouB?: number;
  rsi?: number | null;
  macdLine?: number | null;
  signalLine?: number | null;
  macdHistogram?: number | null;
}

interface StockTechnicalChartProps {
  ticker: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function calcSMA(data: number[], period: number): (number | undefined)[] {
  return data.map((_, i) => {
    if (i < period - 1) return undefined;
    const slice = data.slice(i - period + 1, i + 1);
    return slice.reduce((a, b) => a + b, 0) / period;
  });
}

function calcBollinger(closes: number[], period = 20, mult = 2) {
  const sma = calcSMA(closes, period);
  return closes.map((_, i) => {
    if (i < period - 1 || sma[i] === undefined) return { upper: undefined, middle: undefined, lower: undefined };
    const slice = closes.slice(i - period + 1, i + 1);
    const mean = sma[i]!;
    const variance = slice.reduce((sum, v) => sum + (v - mean) ** 2, 0) / period;
    const stdDev = Math.sqrt(variance);
    return { upper: mean + mult * stdDev, middle: mean, lower: mean - mult * stdDev };
  });
}

function calcIchimoku(highs: number[], lows: number[], closes: number[]) {
  const highLow = (start: number, end: number) => {
    const h = highs.slice(start, end);
    const l = lows.slice(start, end);
    if (!h.length) return undefined;
    return (Math.max(...h) + Math.min(...l)) / 2;
  };

  const len = closes.length;
  const tenkan: (number | undefined)[] = [];
  const kijun: (number | undefined)[] = [];
  const senkouA: (number | undefined)[] = [];
  const senkouB: (number | undefined)[] = [];

  for (let i = 0; i < len; i++) {
    tenkan.push(i >= 8 ? highLow(i - 8, i + 1) : undefined);
    kijun.push(i >= 25 ? highLow(i - 25, i + 1) : undefined);
    const t = tenkan[i], k = kijun[i];
    senkouA.push(t !== undefined && k !== undefined ? (t + k) / 2 : undefined);
    senkouB.push(i >= 51 ? highLow(i - 51, i + 1) : undefined);
  }

  return { tenkan, kijun, senkouA, senkouB };
}

function calcRSI(closes: number[], period = 14): (number | null)[] {
  const result: (number | null)[] = [];
  let avgGain = 0, avgLoss = 0;
  for (let i = 0; i < closes.length; i++) {
    if (i === 0) { result.push(null); continue; }
    const change = closes[i] - closes[i - 1];
    const gain = change > 0 ? change : 0;
    const loss = change < 0 ? -change : 0;
    if (i < period) {
      avgGain += gain / period;
      avgLoss += loss / period;
      result.push(null);
    } else if (i === period) {
      avgGain += gain / period;
      avgLoss += loss / period;
      const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
      result.push(100 - 100 / (1 + rs));
    } else {
      avgGain = (avgGain * (period - 1) + gain) / period;
      avgLoss = (avgLoss * (period - 1) + loss) / period;
      const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
      result.push(100 - 100 / (1 + rs));
    }
  }
  return result;
}

function calcEMA(data: number[], period: number): (number | null)[] {
  const result: (number | null)[] = [];
  const k = 2 / (period + 1);
  let ema: number | null = null;
  for (let i = 0; i < data.length; i++) {
    if (i < period - 1) { result.push(null); continue; }
    if (ema === null) {
      ema = data.slice(0, period).reduce((a, b) => a + b, 0) / period;
    } else {
      ema = data[i] * k + ema * (1 - k);
    }
    result.push(ema);
  }
  return result;
}

function calcMACD(closes: number[]) {
  const ema12 = calcEMA(closes, 12);
  const ema26 = calcEMA(closes, 26);
  const macdLine = ema12.map((v, i) => (v != null && ema26[i] != null ? v - ema26[i]! : null));
  const macdValues = macdLine.filter(v => v != null) as number[];
  const signalRaw = calcEMA(macdValues, 9);
  let si = 0;
  const signalLine = macdLine.map(v => {
    if (v == null) return null;
    const s = signalRaw[si++];
    return s;
  });
  const histogram = macdLine.map((v, i) => (v != null && signalLine[i] != null ? v - signalLine[i]! : null));
  return { macdLine, signalLine, histogram };
}

export function StockTechnicalChart({ ticker, open, onOpenChange }: StockTechnicalChartProps) {
  const [period, setPeriod] = useState<'1m' | '6m' | '1y'>('6m');
  const [rawData, setRawData] = useState<PricePoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRSI, setShowRSI] = useState(false);
  const [showMACD, setShowMACD] = useState(false);
  const [showVolume, setShowVolume] = useState(false);

  const monthsMap = { '1m': 2, '6m': 7, '1y': 14 }; // extra months for indicator warmup

  useEffect(() => {
    if (!open || !ticker) return;
    const fetchPrices = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: fnError } = await supabase.functions.invoke('fetch-price-history', {
          body: { ticker, months: monthsMap[period] }
        });
        if (fnError) throw fnError;
        if (!data?.success) throw new Error(data?.error || 'Failed');
        setRawData(data.prices || []);
      } catch (e: any) {
        setError(e.message);
        setRawData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchPrices();
  }, [open, ticker, period]);

  const chartData = useMemo(() => {
    if (!rawData.length) return [];
    const closes = rawData.map(p => p.close);
    const highs = rawData.map(p => p.high);
    const lows = rawData.map(p => p.low);

    const sma50 = calcSMA(closes, 50);
    const sma200 = calcSMA(closes, 200);
    const bb = calcBollinger(closes);
    const ich = calcIchimoku(highs, lows, closes);
    const rsi = calcRSI(closes);
    const macd = calcMACD(closes);

    const full: ChartDataPoint[] = rawData.map((p, i) => ({
      ...p,
      sma50: sma50[i],
      sma200: sma200[i],
      bbUpper: bb[i].upper,
      bbMiddle: bb[i].middle,
      bbLower: bb[i].lower,
      tenkan: ich.tenkan[i],
      kijun: ich.kijun[i],
      senkouA: ich.senkouA[i],
      senkouB: ich.senkouB[i],
      rsi: rsi[i],
      macdLine: macd.macdLine[i],
      signalLine: macd.signalLine[i],
      macdHistogram: macd.histogram[i],
    }));

    // Trim warmup period based on display
    const displayMonths = { '1m': 1, '6m': 6, '1y': 12 }[period];
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - displayMonths);
    const cutoffStr = cutoff.toISOString().split('T')[0];
    return full.filter(p => p.date >= cutoffStr);
  }, [rawData, period]);

  const hasRSI = chartData.some(d => d.rsi != null);
  const hasMACD = chartData.some(d => d.macdLine != null);
  const lastRsi = chartData.filter(d => d.rsi != null).slice(-1)[0]?.rsi;
  const rsiColor = lastRsi != null && lastRsi >= 70 ? '#ef4444' : lastRsi != null && lastRsi <= 30 ? '#10b981' : '#8b5cf6';

  const priceChange = chartData.length >= 2
    ? ((chartData[chartData.length - 1].close - chartData[0].close) / chartData[0].close * 100)
    : 0;

  const tooltipStyle = {
    backgroundColor: 'hsl(var(--background))',
    border: '1px solid hsl(var(--border))',
    borderRadius: '8px',
    fontSize: '11px',
  };

  const formatXTick = (d: string) => new Date(d).toLocaleDateString('en', { month: 'short', day: 'numeric' });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span className="font-mono text-lg">{ticker}</span>
            <Badge variant={priceChange >= 0 ? 'default' : 'destructive'} className="font-mono">
              {priceChange >= 0 ? '+' : ''}{priceChange.toFixed(2)}%
            </Badge>
            <span className="text-sm text-muted-foreground font-normal">Technical Analysis</span>
          </DialogTitle>
        </DialogHeader>

        <Tabs value={period} onValueChange={(v) => setPeriod(v as any)}>
          <div className="flex items-center gap-4 flex-wrap">
            <TabsList className="grid w-48 grid-cols-3">
              <TabsTrigger value="1m">1M</TabsTrigger>
              <TabsTrigger value="6m">6M</TabsTrigger>
              <TabsTrigger value="1y">1Y</TabsTrigger>
            </TabsList>
            {hasRSI && (
              <div className="flex items-center gap-1.5">
                <Switch id="stc-rsi-toggle" checked={showRSI} onCheckedChange={setShowRSI} className="h-4 w-7 [&>span]:h-3 [&>span]:w-3" />
                <Label htmlFor="stc-rsi-toggle" className="text-[10px] font-medium text-muted-foreground cursor-pointer">RSI</Label>
              </div>
            )}
            {hasMACD && (
              <div className="flex items-center gap-1.5">
                <Switch id="stc-macd-toggle" checked={showMACD} onCheckedChange={setShowMACD} className="h-4 w-7 [&>span]:h-3 [&>span]:w-3" />
                <Label htmlFor="stc-macd-toggle" className="text-[10px] font-medium text-muted-foreground cursor-pointer">MACD</Label>
              </div>
            )}
            <div className="flex items-center gap-1.5">
              <Switch id="stc-vol-toggle" checked={showVolume} onCheckedChange={setShowVolume} className="h-4 w-7 [&>span]:h-3 [&>span]:w-3" />
              <Label htmlFor="stc-vol-toggle" className="text-[10px] font-medium text-muted-foreground cursor-pointer">Vol</Label>
            </div>
          </div>

          <div className="mt-4">
            {loading && (
              <div className="space-y-2">
                <Skeleton className="h-[400px] w-full" />
              </div>
            )}
            {error && <p className="text-sm text-destructive text-center py-8">{error}</p>}
            {!loading && !error && chartData.length > 0 && (
              <div className="space-y-4">
                {/* Main chart */}
                <div className="h-[420px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                        tickFormatter={(d) => new Date(d).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
                        interval="preserveStartEnd"
                        minTickGap={40}
                      />
                      <YAxis
                        yAxisId="price"
                        domain={['auto', 'auto']}
                        tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                        tickFormatter={(v) => `$${v.toFixed(0)}`}
                        width={55}
                      />
                      {showVolume && (
                        <YAxis
                          yAxisId="volume"
                          orientation="right"
                          tick={false}
                          axisLine={false}
                          tickLine={false}
                          width={0}
                          domain={[0, (max: number) => max * 4]}
                        />
                      )}
                      <Tooltip
                        content={({ active, payload, label }) => {
                          if (!active || !payload?.length) return null;
                          const d = payload[0]?.payload as ChartDataPoint;
                          return (
                            <div className="bg-background border border-border rounded-lg px-3 py-2 shadow-xl text-xs space-y-1">
                              <p className="font-medium">{new Date(label).toLocaleDateString('en', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                              <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-muted-foreground">
                                <span>Open: <span className="text-foreground font-mono">${d.open?.toFixed(2)}</span></span>
                                <span>High: <span className="text-foreground font-mono">${d.high?.toFixed(2)}</span></span>
                                <span>Low: <span className="text-foreground font-mono">${d.low?.toFixed(2)}</span></span>
                                <span>Close: <span className="text-foreground font-mono">${d.close?.toFixed(2)}</span></span>
                                {d.sma50 && <span>SMA50: <span className="text-blue-400 font-mono">${d.sma50.toFixed(2)}</span></span>}
                                {d.sma200 && <span>SMA200: <span className="text-orange-400 font-mono">${d.sma200.toFixed(2)}</span></span>}
                                {d.bbUpper && <span>BB Upper: <span className="text-purple-400 font-mono">${d.bbUpper.toFixed(2)}</span></span>}
                                {d.bbLower && <span>BB Lower: <span className="text-purple-400 font-mono">${d.bbLower.toFixed(2)}</span></span>}
                                {showVolume && d.volume != null && <span>Vol: <span className="text-foreground font-mono">{(d.volume / 1e6).toFixed(2)}M</span></span>}
                              </div>
                            </div>
                          );
                        }}
                      />

                      {/* Volume bars */}
                      {showVolume && (
                        <Bar dataKey="volume" yAxisId="volume" fill="hsl(var(--muted-foreground))" fillOpacity={0.15} stroke="none" name="Volume" />
                      )}

                      {/* Ichimoku Cloud (Kumo) */}
                      <Area dataKey="senkouA" yAxisId="price" stroke="none" fill="rgba(34,197,94,0.08)" stackId="kumo" />
                      <Area dataKey="senkouB" yAxisId="price" stroke="none" fill="rgba(239,68,68,0.08)" stackId="kumo2" />

                      {/* Bollinger Bands */}
                      <Line yAxisId="price" dataKey="bbUpper" stroke="#a855f7" strokeWidth={1} dot={false} strokeDasharray="4 2" opacity={0.6} />
                      <Line yAxisId="price" dataKey="bbMiddle" stroke="#a855f7" strokeWidth={1} dot={false} strokeDasharray="2 2" opacity={0.4} />
                      <Line yAxisId="price" dataKey="bbLower" stroke="#a855f7" strokeWidth={1} dot={false} strokeDasharray="4 2" opacity={0.6} />

                      {/* SMA */}
                      <Line yAxisId="price" dataKey="sma50" stroke="#3b82f6" strokeWidth={1.5} dot={false} />
                      <Line yAxisId="price" dataKey="sma200" stroke="#f97316" strokeWidth={1.5} dot={false} />

                      {/* Ichimoku lines */}
                      <Line yAxisId="price" dataKey="tenkan" stroke="#22c55e" strokeWidth={1} dot={false} opacity={0.7} />
                      <Line yAxisId="price" dataKey="kijun" stroke="#ef4444" strokeWidth={1} dot={false} opacity={0.7} />

                      {/* Price */}
                      <Line yAxisId="price" dataKey="close" stroke="#FACC15" strokeWidth={2.5} dot={false} name="Price" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>

                {/* Legend */}
                <div className="flex flex-wrap gap-3 justify-center text-[11px]">
                  <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-[#FACC15] inline-block rounded" /> Price</span>
                  <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-[#3b82f6] inline-block rounded" /> SMA 50</span>
                  <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-[#f97316] inline-block rounded" /> SMA 200</span>
                  <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-[#a855f7] inline-block rounded" /> Bollinger</span>
                  <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-[#22c55e] inline-block rounded" /> Tenkan</span>
                  <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-[#ef4444] inline-block rounded" /> Kijun</span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 bg-green-500/10 border border-green-500/30 inline-block rounded-sm" /> Kumo
                  </span>
                </div>

                {/* RSI Sub-Chart */}
                {showRSI && hasRSI && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-muted-foreground">RSI (14)</span>
                      {lastRsi != null && (
                        <span className={`text-xs font-bold ${lastRsi >= 70 ? 'text-destructive' : lastRsi <= 30 ? 'text-emerald-500' : 'text-muted-foreground'}`}>
                          {lastRsi.toFixed(1)} {lastRsi >= 70 ? '⬆ Overbought' : lastRsi <= 30 ? '⬇ Oversold' : ''}
                        </span>
                      )}
                    </div>
                    <ResponsiveContainer width="100%" height={100}>
                      <ComposedChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.2} />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={formatXTick} hide />
                        <YAxis tick={{ fontSize: 10 }} domain={[0, 100]} ticks={[0, 30, 50, 70, 100]} width={55} />
                        <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => [value?.toFixed(1) ?? '-', 'RSI']} labelFormatter={(l) => new Date(l).toLocaleDateString('en', { month: 'long', day: 'numeric', year: 'numeric' })} />
                        <ReferenceLine y={70} stroke="#ef4444" strokeDasharray="3 3" />
                        <ReferenceLine y={50} stroke="hsl(var(--muted-foreground))" strokeDasharray="6 3" strokeOpacity={0.4} />
                        <ReferenceLine y={30} stroke="#10b981" strokeDasharray="3 3" />
                        <Area type="monotone" dataKey="rsi" stroke="none" fill="#ef4444" fillOpacity={0.05} baseValue={70} />
                        <Area type="monotone" dataKey="rsi" stroke="none" fill="#10b981" fillOpacity={0.05} baseValue={30} />
                        <Line type="monotone" dataKey="rsi" stroke={rsiColor} strokeWidth={1.5} dot={false} name="RSI" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* MACD Sub-Chart */}
                {showMACD && hasMACD && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-muted-foreground">MACD (12, 26, 9)</span>
                    </div>
                    <ResponsiveContainer width="100%" height={120}>
                      <ComposedChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.2} />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={formatXTick} hide />
                        <YAxis tick={{ fontSize: 10 }} width={55} />
                        <Tooltip contentStyle={tooltipStyle} formatter={(value: number, name: string) => [value?.toFixed(4) ?? '-', name]} labelFormatter={(l) => new Date(l).toLocaleDateString('en', { month: 'long', day: 'numeric', year: 'numeric' })} />
                        <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" strokeDasharray="3 3" strokeOpacity={0.5} />
                        <Bar dataKey="macdHistogram" name="Histogram">
                          {chartData.map((entry, index) => (
                            <Cell key={index} fill={(entry.macdHistogram ?? 0) >= 0 ? '#22c55e' : '#ef4444'} fillOpacity={0.6} />
                          ))}
                        </Bar>
                        <Line type="monotone" dataKey="macdLine" stroke="#3b82f6" strokeWidth={1.5} dot={false} name="MACD" />
                        <Line type="monotone" dataKey="signalLine" stroke="#f97316" strokeWidth={1.5} dot={false} name="Signal" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Current indicators summary */}
                {chartData.length > 0 && (() => {
                  const last = chartData[chartData.length - 1];
                  return (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                      <div className="bg-muted/30 rounded-lg px-3 py-2">
                        <span className="text-muted-foreground">Close</span>
                        <p className="font-mono font-bold text-foreground">${last.close.toFixed(2)}</p>
                      </div>
                      {last.sma50 && (
                        <div className="bg-muted/30 rounded-lg px-3 py-2">
                          <span className="text-muted-foreground">SMA 50</span>
                          <p className="font-mono font-bold" style={{ color: last.close > last.sma50 ? '#22c55e' : '#ef4444' }}>
                            ${last.sma50.toFixed(2)}
                          </p>
                        </div>
                      )}
                      {last.sma200 && (
                        <div className="bg-muted/30 rounded-lg px-3 py-2">
                          <span className="text-muted-foreground">SMA 200</span>
                          <p className="font-mono font-bold" style={{ color: last.close > last.sma200 ? '#22c55e' : '#ef4444' }}>
                            ${last.sma200.toFixed(2)}
                          </p>
                        </div>
                      )}
                      {last.bbUpper && last.bbLower && (
                        <div className="bg-muted/30 rounded-lg px-3 py-2">
                          <span className="text-muted-foreground">BB Width</span>
                          <p className="font-mono font-bold text-purple-400">
                            {((last.bbUpper - last.bbLower) / (last.bbMiddle || 1) * 100).toFixed(1)}%
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
